import * as React from "react";
import Link from "next/link";
import { PasswordValidation } from "@nju/feature-portal-registration";
import { ErrorMessage, Input } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, Button, Heading, Separator } from "@nju/ui";
import { IContext } from "../changePassword.machine";

interface IChangePasswordFormProps {
  context: IContext;
  isLoading: boolean;
  handleSubmit: React.FormEventHandler<HTMLFormElement>;
  handleOldPasswordChange: React.ChangeEventHandler<HTMLInputElement>;
  handleNewPasswordChange: React.ChangeEventHandler<HTMLInputElement>;
  handleRepeatNewPasswordChange: React.ChangeEventHandler<HTMLInputElement>;
}

export function ChangePasswordForm({
  context,
  isLoading,
  handleSubmit,
  handleOldPasswordChange,
  handleNewPasswordChange,
  handleRepeatNewPasswordChange,
}: IChangePasswordFormProps) {
  const [showOldPassword, seShowOldPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showRepeatNewPassword, setShowRepeatNewPassword] =
    React.useState(false);

  const {
    oldPasswordError,
    newPasswordError,
    repeatNewPasswordError,
    newPassword,
  } = context;

  return (
    <>
      <Heading font="ttfirs" tone="neutral" variant="h3" weight="bold">
        ustaw nowe hasło
      </Heading>
      <Separator size="m" tone="invisible" />
      <form autoComplete="false" onSubmit={handleSubmit}>
        <div className="max-w-[505px]">
          <Input
            label="wpisz aktualne hasło"
            name="currentPassword"
            onChange={handleOldPasswordChange}
            state={oldPasswordError ? "error" : undefined}
            suffix={
              <div
                className="flex items-center cursor-pointer"
                data-test-id="currentPasswordShow"
                onClick={() => {
                  seShowOldPassword((isVisible) => {
                    return !isVisible;
                  });
                }}
                role="button"
              >
                <Icon
                  height={24}
                  name={showOldPassword ? "eye" : "eye-slash"}
                  width={30}
                />
              </div>
            }
            type={showOldPassword ? "text" : "password"}
          />
          {oldPasswordError && <ErrorMessage message={oldPasswordError} />}
          <Separator size="xs" tone="invisible" />
          <Input
            autoComplete="new-password"
            label="wpisz nowe hasło"
            name="newPassword"
            onChange={handleNewPasswordChange}
            state={newPasswordError ? "error" : undefined}
            suffix={
              <div
                className="flex items-center cursor-pointer"
                data-test-id="newPasswordShow"
                onClick={() => {
                  setShowNewPassword((isVisible) => {
                    return !isVisible;
                  });
                }}
                role="button"
              >
                <Icon
                  height={24}
                  name={showNewPassword ? "eye" : "eye-slash"}
                  width={30}
                />
              </div>
            }
            type={showNewPassword ? "text" : "password"}
          />
          {newPasswordError && <ErrorMessage message={newPasswordError} />}
          <Separator size="xs" tone="invisible" />
          <Input
            autoComplete="new-password"
            label="jeszcze raz wpisz nowe hasło"
            name="repeatNewPassword"
            onChange={handleRepeatNewPasswordChange}
            state={repeatNewPasswordError ? "error" : undefined}
            suffix={
              <div
                className="flex items-center cursor-pointer"
                data-test-id="repeatNewPasswordShow"
                onClick={() => {
                  setShowRepeatNewPassword((isVisible) => {
                    return !isVisible;
                  });
                }}
                role="button"
              >
                <Icon
                  height={24}
                  name={showRepeatNewPassword ? "eye" : "eye-slash"}
                  width={30}
                />
              </div>
            }
            type={showRepeatNewPassword ? "text" : "password"}
          />
          {repeatNewPasswordError && (
            <ErrorMessage message={repeatNewPasswordError} />
          )}
        </div>
        <Separator size="xs" tone="invisible" />
        <PasswordValidation value={newPassword} />
        <Separator size="m" tone="invisible" />
        <div className="flex only-mobile:flex-col-reverse from-tablet:flex-row from-tablet:justify-between">
          <Link
            href={portalRoutes.mojeKonto.mojProfil.index.get()}
            passHref={true}
          >
            <A
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
              variant="secondary"
            >
              anuluj
            </A>
          </Link>
          <Separator
            size={{
              "@onlyMobile": "xxs",
              "@fromTablet": "none",
            }}
            tone="invisible"
          />
          <Button
            isLoading={isLoading}
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
            testId="save-new-password"
            type="submit"
          >
            zapisz
          </Button>
        </div>
      </form>
    </>
  );
}
